import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  X,
  ChevronDown,
  Zap,
  Shield,
  Target,
  TrendingUp,
  Users,
  Sparkles,
  Award,
  Clock,
  Volume2,
  ChevronLeft,
  ChevronRight,
  Quote,
} from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { TestimonialCarousel } from '../components/ServiceDetailExtras';
import './HomePage.funnel.css';

const CAL_URL = 'https://calendly.com/thecollinsacademynew/30min';
const CHECKOUT_ANNUAL_URL = 'https://www.commas.com/checkout/mgmGR4XvWZfjh3bJm';
const CHECKOUT_MONTHLY_URL = 'https://www.commas.com/checkout/EmEGW4u4gS83YyW';

/* -------- 3 headline pillar services (top offers) -------- */
const PILLARS = [
  {
    num: '01',
    tag: 'Wealth Rebuild System™',
    title: '75-Day Financial Transformation',
    prose:
      "Our signature 75-day intensive focused on aggressive credit repair, student loan removal, and complete deletion of negative items — collections, charge-offs, late payments, and inquiries — across all three bureaus. From a damaged file to funding-ready in less than three months.",
    image: '/founder/75day.png',
    bullets: [
      'Aggressive 3-bureau credit repair campaign',
      'Student loan removal (federal, private, and defaulted loans)',
      'Full deletion of collections, charge-offs, late payments, and inquiries',
      'Weekly 1-on-1 strategy call with the CEO',
      'Funding-ready file in 75 days — guaranteed trajectory',
    ],
    cta: { label: 'See the 75-Day Plan', href: '/credit-repair' },
  },
  {
    num: '02',
    tag: 'Elite $250K Package',
    title: 'Complete Credit Repair & Funding Blueprint',
    prose:
      "A full done-for-you credit restoration engine paired with our funding readiness playbook. FCRA-compliant disputes across all three bureaus, tradeline layering, and lender-ready file staging so you show up to underwriting with a $250K position — not a wish list.",
    image: '/founder/office-cta.png',
    bullets: [
      'Unlimited done-for-you credit repair across all 3 bureaus',
      '$10,000 primary tradeline + $80K in aged AU spots layered on your file',
      'Business entity structuring + EIN + bank account setup',
      'Up to $250,000 in business funding deployed through our lender network',
      'FREE trust creation for asset protection',
    ],
    cta: { label: 'See the Elite Package', href: '/#complete-stack' },
  },
  {
    num: '03',
    tag: 'AI Business Autopilot',
    title: 'Systems That Sell While You Sleep',
    prose:
      "Custom AI-powered business systems that run sales, support, and operations 24/7. Pick the Gold Starter for a full launch stack or the Platinum full-autopilot suite — offers structured, ads dialed, funnels live, and follow-up on autopilot.",
    image: '/founder/ai-autopilot.png',
    bullets: [
      'Custom AI sales agents that book calls, answer DMs, and close 24/7',
      'Done-for-you offer stack + sales funnel + automated follow-up sequences',
      'Paid ads built, launched, and optimized by our media team',
      'Branded professional website + full brand kit (yours to keep)',
      'Private AI playbooks and prompts used by our 7-figure clients',
    ],
    cta: { label: 'Compare Gold vs Platinum', href: '/#complete-stack' },
  },
  {
    num: '04',
    tag: 'Bonus · $1 Trial',
    title: 'Perfect Credit Monitoring',
    prose:
      "Try the same 3-bureau monitoring stack our private clients run — daily alerts, dark-web surveillance, and a $1M identity-theft policy. Full access for 7 days, just $1. Cancel anytime.",
    image: '/founder/credit-monitoring.png',
    cta: { label: 'Start My $1 Trial', href: '/perfect-credit-monitoring' },
    bonus: true,
  },
];

const BEFORE = [
  'Denied for basic credit lines and stuck watching others get approved',
  'No clue which disputes actually work and which just waste time',
  'Trying to piece together funding with 20 tabs open and zero strategy',
  'No system to convert your knowledge or hustle into real recurring income',
];

const AFTER = [
  'A funding-ready profile lenders actually approve — not a hope-and-pray file',
  'A dispute engine handled for you — negatives, collections, and student loans deleted',
  'A clear capital roadmap — repair, tradelines, entity, and lender stack aligned',
  'Up to $250,000 in business funding deployed through our lender network',
];

const WHO = [
  { icon: Target, text: 'Entrepreneurs sick of self-funding every move out of pocket' },
  { icon: TrendingUp, text: '6-figure earners ready to unlock institutional capital and scale' },
  { icon: Zap, text: 'Coaches, creators, and consultants ready to package the offer right' },
  { icon: Users, text: 'Anyone tired of watching “gurus” gatekeep the actual playbook' },
  { icon: Award, text: 'Serious action-takers ready to execute — not another tire-kicker' },
];

const STACK = [
  'Unlimited done-for-you credit repair across all three bureaus',
  'Student loan removal service (federal, private, and defaulted loans)',
  'Weekly 1-on-1 strategy call with the CEO (private, direct, on your calendar)',
  'Monthly live meet-ups hosted at one of the CEO\'s residences',
  '$10,000 primary tradeline + access to $20K+ in additional primaries',
  'Up to $250,000 in business funding deployed through our lender network',
  'Custom-built professional website with AI-powered funnel, follow-up & offer stack (yours to keep)',
  'Done-for-you digital product creation — course, offer, or digital asset built and packaged to sell',
  'Your own niche book or documentary written for you and published on Amazon',
  'Every Collins Academy book, guide, and playbook — complete library included',
  'FREE trust creation & structuring (asset protection built-in)',
  'Hands-on assistance acquiring cash-flowing businesses with your newly built capital',
  'Real-estate & investment property funding walk-through with our lender network',
  'Business bank-account setup + banking relationship intros',
  'The Collins Academy ChatGPT Dispute Prompt (unlimited letters)',
  '75-day guided Wealth Rebuild coaching sprint',
  'Hundreds of thousands of dollars in social media reach + promotion across our channels',
  'Private access to lender contacts, entity structuring, and funding rounds',
  'Access to our private client community and monthly live strategy calls',
];

const TESTIMONIALS = [
  {
    initials: 'TR',
    color: '#C1272D',
    name: 'Tasha R.',
    location: 'Atlanta, GA',
    result: 'Funded $87K in 60 days',
    quote:
      "I went from three collections and a 540 score to a real approvable file. The Collins team ran the disputes and handed me the exact lender list to hit — first funding round closed in month two.",
  },
  {
    initials: 'MJ',
    color: '#B8892E',
    name: 'Marcus J.',
    location: 'Houston, TX',
    result: 'AI Autopilot booked 41 calls / mo',
    quote:
      "The Platinum Autopilot build changed my business. Funnel, follow-up and the offer stack — set up in a week. My phone rings without me touching an ad account.",
  },
  {
    initials: 'DP',
    color: '#0F0F14',
    name: 'Danielle P.',
    location: 'Charlotte, NC',
    result: '158-point score jump',
    quote:
      "Real strategy, real results. 75 days later my middle score jumped 158 points, I placed a $10K primary, and I finally understand how the whole capital stack works. Worth every dollar.",
  },
  {
    initials: 'KS',
    color: '#8E1A20',
    name: 'Kevin S.',
    location: 'Miami, FL',
    result: 'Won $14,200 diminished value claim',
    quote:
      "Insurance tried to give me $1,800 for a totaled Tesla. Collins Academy's recovery team fought it and got me $14,200. Direct deposit hit before the coaching call ended.",
  },
];

const FAQS = [
  {
    q: 'How is this different from other credit repair companies?',
    a: "We are not a mill. Every file is engineered for funding — not just deletions. Disputes, tradelines, entity structure, and lender staging are treated as one system. You leave repair with an approvable profile, not just a cleaner report.",
  },
  {
    q: 'How fast can I see real results?',
    a: "Most clients see initial removals in the first 30–45 days and hit funding-ready posture inside our 75-day Wealth Rebuild sprint. Timelines vary based on your starting file — we set realistic expectations on the strategy call.",
  },
  {
    q: 'Do I have to buy every service at once?',
    a: "No. Every service on this page works standalone. Start with what fits — the $250K Package, AI Autopilot, Insurance Recovery, or even the $37 ChatGPT Dispute Prompt — and stack from there.",
  },
  {
    q: 'What if I already tried credit repair and it did not work?',
    a: "That is exactly who we built this for. Most repair services stop at deletions and never engineer the file for lender approval. We rebuild the whole system — dispute engine, tradelines, entity, and lender staging — so the score actually converts into capital.",
  },
  {
    q: 'Is there a refund policy?',
    a: "Because of the volume of custom work per client (dispute drafting, tradeline placement, coaching hours), most services are non-refundable once execution begins. We are transparent about scope and outcomes on the intake call so both sides go in eyes open.",
  },
  {
    q: 'How do I get started?',
    a: "Book a private strategy call using the button on this page. On the call we audit your file, pick the right service (or stack), and give you a step-by-step plan — no pressure, no upsell theatre.",
  },
];

const PARTNERS = [
  { name: 'SmartCredit', logo: '/logos/smartcredit.svg' },
  { name: 'Nav', logo: '/logos/nav.svg' },
  { name: 'Credit Repair Cloud', logo: '/logos/credit-repair-cloud.svg' },
  { name: 'Bank of America', logo: '/logos/bank-of-america.svg' },
  { name: 'Chase', logo: '/logos/chase.svg' },
  { name: 'Equifax', logo: '/logos/equifax.svg' },
];

const CLIENT_WINS = [
  {
    img: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_f39e6fee-79f2-4684-89ed-32979a04e68f/artifacts/ac325d1ff9238a2b_IMG_9952.webp',
    quote: 'Walked into the Collins Academy stressed about my credit — walked out of the SmartCredit conference with a plan, a team, and real momentum.',
  },
  {
    img: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_f39e6fee-79f2-4684-89ed-32979a04e68f/artifacts/ce4896d6f841d325_IMG_9374.jpg',
    quote: 'They rebuilt my file and I finally got the funding I had been chasing for years.',
  },
  {
    img: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_f39e6fee-79f2-4684-89ed-32979a04e68f/artifacts/cc4da6fef81f8b31_IMG_9376.jpg',
    quote: 'The 75-Day plan was no joke — my scores jumped and my business line got approved.',
  },
  {
    img: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_f39e6fee-79f2-4684-89ed-32979a04e68f/artifacts/3f5aea8b6f765244_IMG_9385.jpg',
    quote: 'Working with the Academy leveled up my whole brand — and my credit position matched it.',
  },
  {
    img: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_f39e6fee-79f2-4684-89ed-32979a04e68f/artifacts/ac64ca10e94b9da3_IMG_9387.jpg',
    quote: 'Repaired my credit, cleaned up my funding profile, and connected me to the right lenders.',
  },
];

const TcfPartnersMarquee = () => (
  <div className="tcf-partners" data-testid="tcf-partners" aria-label="Partner network">
    <span className="tcf-partners-eyebrow">Partners</span>
    <div className="tcf-partners-track-wrap">
      <div className="tcf-partners-track">
        {[...PARTNERS, ...PARTNERS, ...PARTNERS].map((p, i) => (
          <span className="tcf-partners-item" key={`${p.name}-${i}`}>
            <img
              src={p.logo}
              alt={p.name}
              className="tcf-partners-logo"
              loading="lazy"
            />
          </span>
        ))}
      </div>
    </div>
  </div>
);

const TcfClientCarousel = () => {
  const autoplay = useRef(
    Autoplay({ delay: 4200, stopOnInteraction: false, stopOnMouseEnter: true })
  );
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'start', slidesToScroll: 1 },
    [autoplay.current]
  );

  const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
  const scrollNext = () => emblaApi && emblaApi.scrollNext();

  return (
    <section className="tcf-clients" data-testid="tcf-clients">
      <div className="tcf-clients-head">
        <span className="tcf-section-eyebrow">Real Clients · Real Wins</span>
        <h2 className="tcf-section-title">
          The people we actually serve.
        </h2>
        <p className="tcf-section-sub">
          Private clients, business owners, and professionals walking through the Collins Academy
          doors — getting their credit, capital and AI stack done in person and done right.
        </p>
      </div>

      <div className="tcf-clients-viewport" ref={emblaRef}>
        <div className="tcf-clients-container">
          {CLIENT_WINS.map((c, i) => (
            <article className="tcf-client-card" key={i} data-testid={`client-win-${i}`}>
              <div className="tcf-client-media">
                <img src={c.img} alt="Client win" loading="lazy" />
              </div>
              <figcaption className="tcf-client-caption">
                <Quote size={14} className="tcf-client-quote-icon" />
                <p className="tcf-client-quote">{c.quote}</p>
              </figcaption>
            </article>
          ))}
        </div>
      </div>

      <div className="tcf-clients-controls">
        <button
          type="button"
          className="tcf-clients-btn"
          onClick={scrollPrev}
          aria-label="Previous client"
          data-testid="client-prev"
        >
          <ChevronLeft size={20} strokeWidth={2.4} />
        </button>
        <button
          type="button"
          className="tcf-clients-btn"
          onClick={scrollNext}
          aria-label="Next client"
          data-testid="client-next"
        >
          <ChevronRight size={20} strokeWidth={2.4} />
        </button>
      </div>
    </section>
  );
};

const VIP_SHOTS = [
  '/founder/vip/vip5.webp',
  '/founder/vip/vip7.jpg',
  '/founder/vip/vip1.webp',
  '/founder/vip/vip6.jpg',
  '/founder/vip/vip3.webp',
  '/founder/vip/vip9.jpg',
  '/founder/vip/vip2.jpg',
  '/founder/vip/vip8.jpg',
  '/founder/vip/vip4.jpg',
];

const VipGalleryCarousel = () => {
  const autoplay = useRef(
    Autoplay({ delay: 3800, stopOnInteraction: false, stopOnMouseEnter: true })
  );
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'start', slidesToScroll: 1, dragFree: true },
    [autoplay.current]
  );

  const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
  const scrollNext = () => emblaApi && emblaApi.scrollNext();

  return (
    <div className="tcf-vipgallery-wrap" data-testid="tcf-vipgallery-carousel">
      <div className="tcf-vipgallery-viewport" ref={emblaRef}>
        <div className="tcf-vipgallery-track">
          {VIP_SHOTS.map((src, i) => (
            <figure className="tcf-vipgal-tile" key={`${src}-${i}`}>
              <img src={src} alt="" loading="lazy" />
            </figure>
          ))}
        </div>
      </div>

      <div className="tcf-vipgallery-controls">
        <button
          type="button"
          className="tcf-vipgallery-btn"
          onClick={scrollPrev}
          aria-label="Previous photo"
          data-testid="vip-prev"
        >
          <ChevronLeft size={20} strokeWidth={2.4} />
        </button>
        <button
          type="button"
          className="tcf-vipgallery-btn"
          onClick={scrollNext}
          aria-label="Next photo"
          data-testid="vip-next"
        >
          <ChevronRight size={20} strokeWidth={2.4} />
        </button>
      </div>
    </div>
  );
};

const FounderVideo = () => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const handleUnmute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    v.volume = 1;
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
    setMuted(false);
  };

  return (
    <div className="tcf-video-wrap" data-testid="tcf-video-wrap">
      <video
        ref={videoRef}
        src="/founder/hero.mp4"
        poster="/founder/hero-poster.jpg"
        autoPlay
        muted
        loop
        playsInline
        controls
        preload="auto"
        onVolumeChange={(e) => setMuted(e.currentTarget.muted)}
        data-testid="hero-video"
      />
      {muted && (
        <button
          type="button"
          className="tcf-video-unmute"
          onClick={handleUnmute}
          aria-label="Tap to unmute"
          data-testid="hero-video-unmute"
        >
          <Volume2 size={18} strokeWidth={2.4} />
          <span>Tap to unmute</span>
        </button>
      )}
    </div>
  );
};

const HomePage = () => {
  const [openFaq, setOpenFaq] = useState(0);
  const [stickyVisible, setStickyVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setStickyVisible(window.scrollY > 720);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="tcf" data-testid="tcf-root">
      {/* Fast-action banner */}
      <Link
        to="/vip-day"
        className="tcf-alert tcf-alert-link"
        data-testid="tcf-alert"
      >
        <strong>Pay In Full By Tonight</strong> and receive a VIP Day with the CEO →
      </Link>

      {/* Partner marquee */}
      <TcfPartnersMarquee />

      {/* Hero */}
      <section className="tcf-hero" data-testid="tcf-hero">
        <div className="tcf-hero-grid">
          <div>
            <span className="tcf-eyebrow" data-testid="tcf-eyebrow">
              <span className="tcf-eyebrow-dot" />
              The Collins Academy · Founder-Led
            </span>
            <h1 className="tcf-hero-title">
              Build Real Wealth With <span className="accent">Credit</span>,{' '}
              <span className="gold">Capital</span>, and Systems.
            </h1>
            <p className="tcf-hero-sub">
              We do not sell theory. We repair the credit, place the tradelines, structure the
              entity, and hand you the funding stack that actually gets approved and deployed.
              Real playbook. Real capital. Real room.
            </p>
          </div>
          <div className="tcf-hero-media" data-testid="tcf-hero-media">
            <img
              src="/founder/hero-header.png"
              alt="The Collins Academy — Learn, Build, Grow, Win"
            />
            <div className="tcf-hero-badge">
              <span className="tcf-hero-badge-num">$35M+</span>
              <span className="tcf-hero-badge-txt">
                in client credit lines, tradelines, and business funding placed
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Before / After */}
      <section className="tcf-ba" data-testid="tcf-ba">
        <div className="tcf-ba-inner">
          <span className="tcf-section-eyebrow">The Transformation</span>
          <h2 className="tcf-section-title">
            Where you are <span className="red">now</span> vs. where you land <span className="gold">after.</span>
          </h2>
          <p className="tcf-section-sub">
            The gap between struggling and scaling is not more hustle. It is the right system.
            Here is what changes when you plug into The Collins Academy.
          </p>
          <div className="tcf-ba-grid">
            <div className="tcf-ba-col before">
              <span className="tcf-ba-tag before">Before</span>
              <h3>Grinding Alone</h3>
              <ul className="tcf-ba-list">
                {BEFORE.map((b) => (
                  <li key={b} data-testid={`before-${b.slice(0, 20)}`}>
                    <X size={18} color="#E63946" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="tcf-ba-arrow" aria-hidden="true">
              <ArrowRight size={40} />
            </div>
            <div className="tcf-ba-col after">
              <span className="tcf-ba-tag after">After The Academy</span>
              <h3>Moving Like The 1%</h3>
              <ul className="tcf-ba-list">
                {AFTER.map((a) => (
                  <li key={a} data-testid={`after-${a.slice(0, 20)}`}>
                    <Check size={18} color="#E8B84A" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Client wins carousel */}
      <TcfClientCarousel />

      {/* Real receipts — screenshot testimonial wall */}
      <TestimonialCarousel
        eyebrow="Real Receipts · Real Clients"
        title={<>Credit lifts. Loan payoffs. <span className="sde-gold">$0 balances.</span></>}
        subtitle="Actual screenshots, text messages and discharge letters from real Collins Academy clients — not stock photos, not actors."
      />

      {/* Pillars */}
      <section className="tcf-pillars" data-testid="tcf-pillars">
        <div className="tcf-pillars-head">
          <span className="tcf-section-eyebrow">Our Top Services</span>
          <h2 className="tcf-section-title">
            Three engines. One wealth stack.
          </h2>
          <p className="tcf-section-sub">
            Pick the piece you need — or stack all three. Each service works standalone and layers
            into the same funding-ready playbook we run for every private client.
          </p>
        </div>
        <div className="tcf-pillars-grid">
          {PILLARS.map((p) => (
            <article
              key={p.num}
              className={`tcf-pillar${p.bonus ? ' tcf-pillar-bonus' : ''}`}
              data-testid={`pillar-${p.num}`}
            >
              {p.bonus && <span className="tcf-pillar-ribbon">$1 · 7-DAY TRIAL</span>}
              <div className="tcf-pillar-media">
                <img src={p.image} alt={p.title} loading="lazy" />
                {p.caption && (
                  <span className="tcf-pillar-caption" data-testid={`pillar-${p.num}-caption`}>
                    {p.caption}
                  </span>
                )}
              </div>
              <div className="tcf-pillar-body">
                <span className="tcf-pillar-num">{p.num}</span>
                <p className="tcf-pillar-tag">{p.tag}</p>
                <h3>{p.title}</h3>
                <p>{p.prose}</p>
                {p.bullets && (
                  <ul className="tcf-pillar-bullets" data-testid={`pillar-${p.num}-bullets`}>
                    {p.bullets.map((b, idx) => (
                      <li key={idx}>
                        <span className="tcf-pillar-dot" aria-hidden="true" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {p.cta.href.includes('#') ? (
                  <a
                    href={p.cta.href}
                    className="tcf-pillar-cta"
                    data-testid={`pillar-${p.num}-cta`}
                    onClick={(e) => {
                      const id = p.cta.href.split('#')[1];
                      const el = document.getElementById(id);
                      if (el) {
                        e.preventDefault();
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                  >
                    {p.cta.label} <ArrowRight size={16} />
                  </a>
                ) : (
                  <Link
                    to={p.cta.href}
                    className="tcf-pillar-cta"
                    data-testid={`pillar-${p.num}-cta`}
                  >
                    {p.cta.label} <ArrowRight size={16} />
                  </Link>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Founder video */}
      <section className="tcf-video" data-testid="tcf-video">
        <div className="tcf-video-inner">
          <div className="tcf-video-copy">
            <span className="tcf-section-eyebrow">Watch · 27 Seconds</span>
            <h2 className="tcf-section-title">
              Straight from <span className="red">the founder.</span>
            </h2>
            <p className="tcf-video-lead">
              No fluff, no script — just how The Collins Academy really moves. Press play, then
              decide if you want the stack.
            </p>
          </div>
          <div className="tcf-video-frame">
            <FounderVideo />
          </div>
        </div>
      </section>

      {/* Community */}
      <section className="tcf-community" data-testid="tcf-community">
        <div className="tcf-community-grid">
          <div className="tcf-community-media" data-testid="tcf-community-media">
            <img
              src="/founder/facebook-community.png"
              alt="The Collins Academy 700 Club Facebook community preview"
              loading="lazy"
            />
          </div>
          <div className="tcf-community-body">
            <span className="tcf-section-eyebrow">Free Community · Private Group</span>
            <h2 className="tcf-section-title">
              Join the <span className="gold">700 Club</span> — our free Facebook family of
              <span className="accent"> 2,000+ members</span>.
            </h2>
            <p className="tcf-section-sub">
              Daily score drops, lender hacks, dispute wins, and real conversations with
              founders who are rebuilding their files in public. Zero cost, zero fluff — just
              the room before you buy the room.
            </p>

            <ul className="tcf-community-points" data-testid="tcf-community-points">
              <li><span className="tcf-pillar-dot" aria-hidden="true" /> Live wins and score bumps posted daily</li>
              <li><span className="tcf-pillar-dot" aria-hidden="true" /> Direct access to the Collins Academy team</li>
              <li><span className="tcf-pillar-dot" aria-hidden="true" /> Private threads on lender picks, repair hacks & funding</li>
              <li><span className="tcf-pillar-dot" aria-hidden="true" /> First look at pop-up giveaways and VIP seats</li>
            </ul>

            <div className="tcf-community-ctas">
              <a
                href="https://www.facebook.com/groups/thecollinsacademy700club"
                target="_blank"
                rel="noopener noreferrer"
                className="tcf-btn tcf-btn-primary tcf-btn-lg"
                data-testid="community-cta-join"
              >
                Join The 700 Club — Free <ArrowRight size={18} />
              </a>
              <span className="tcf-community-note">
                <strong>2,300+</strong> members · growing weekly
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Value Stack */}
      <section className="tcf-stack" id="complete-stack" data-testid="tcf-stack">
        <div className="tcf-stack-inner">
          <div className="tcf-stack-head">
            <span className="tcf-section-eyebrow">Here's What You Get</span>
            <h2 className="tcf-section-title">
              The complete Collins Academy stack.
            </h2>
          </div>
          <div className="tcf-stack-card">
            <ul className="tcf-stack-list">
              {STACK.map((s) => (
                <li key={s} data-testid={`stack-${s.slice(0, 18)}`}>
                  <span className="tcf-stack-check">
                    <Check size={14} strokeWidth={3} />
                  </span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
            <div className="tcf-stack-value">
              <div className="tcf-stack-value-label">Full retail value</div>
              <div className="tcf-stack-value-strike">$58,500</div>
              <div className="tcf-price-grid" data-testid="price-grid">
                <a
                  href={CHECKOUT_ANNUAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tcf-price-plan tcf-price-plan-featured"
                  data-testid="price-annual"
                >
                  <span className="tcf-price-plan-tag">Best Value · Save $2,000</span>
                  <div className="tcf-price-plan-name">Pay In Full</div>
                  <div className="tcf-price-plan-sub">one-time · full year of access</div>
                  <div className="tcf-price-plan-bonus" data-testid="price-annual-bonus">
                    ★ Includes VIP Day with the CEO ($2,500 value)
                  </div>
                  <span className="tcf-price-plan-cta">Buy Now <ArrowRight size={14} /></span>
                </a>
                <a
                  href={CHECKOUT_MONTHLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tcf-price-plan"
                  data-testid="price-monthly"
                >
                  <span className="tcf-price-plan-tag alt">Flexible</span>
                  <div className="tcf-price-plan-name">Monthly Plan</div>
                  <div className="tcf-price-plan-sub">12 months of full access</div>
                  <span className="tcf-price-plan-cta">Start Monthly <ArrowRight size={14} /></span>
                </a>
              </div>
            </div>
            <div className="tcf-stack-cta">
              <a
                href={CAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="tcf-btn tcf-btn-primary tcf-btn-lg"
                data-testid="stack-cta"
              >
                Book My Strategy Call <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* VIP Day gallery */}
      <section className="tcf-vipgallery" data-testid="tcf-vipgallery">
        <div className="tcf-vipgallery-head">
          <span className="tcf-section-eyebrow">VIP Day · In-Person Access</span>
          <h2 className="tcf-section-title">
            Behind the velvet rope at <span className="name">The Collins Academy.</span>
          </h2>
          <p className="tcf-section-sub">
            Pay-in-full members unlock a private VIP Day with the CEO — the room where real
            relationships, lender introductions, and seven-figure plays actually happen.
          </p>
        </div>

        <VipGalleryCarousel />

        <div className="tcf-vipgallery-cta">
          <Link
            to="/vip-day"
            className="tcf-btn tcf-btn-primary tcf-btn-lg"
            data-testid="vipgallery-cta"
          >
            Unlock The VIP Day <ArrowRight size={18} />
          </Link>
          <span className="tcf-vipgallery-note">Included with the Pay-in-Full option</span>
        </div>
      </section>

      {/* Founder */}
      <section className="tcf-founder" data-testid="tcf-founder">
        <div className="tcf-founder-grid">
          <div className="tcf-founder-media">
            <img src="/founder/office-cta.png" alt="LaMarqus Collins" />
          </div>
          <div className="tcf-founder-body">
            <span className="tcf-section-eyebrow">Meet Your Guide</span>
            <h2 className="tcf-section-title">
              Direct access to <span className="name">LaMarqus Collins.</span>
            </h2>
            <p>
              LaMarqus is the founder of The Collins Academy, a credit and capital strategist,
              and author of <em>Unlock Business Millions</em>. He built the same playbook we now
              deploy for private clients — going from denied for a starter card to placing
              multi-million-dollar funding rounds for business owners across the country.
            </p>
            <p>
              This is not affiliate resell. Every service on this page is built, executed, and
              coached by the Collins Academy team — with LaMarqus personally in the room on
              strategy calls, funding audits, and Wealth Rebuild sprints.
            </p>
            <div className="tcf-founder-stats">
              <div>
                <div className="tcf-founder-stat-num">$35M+</div>
                <div className="tcf-founder-stat-lbl">Client capital placed</div>
              </div>
              <div>
                <div className="tcf-founder-stat-num">271K+</div>
                <div className="tcf-founder-stat-lbl">Community reach</div>
              </div>
              <div>
                <div className="tcf-founder-stat-num">75d</div>
                <div className="tcf-founder-stat-lbl">Signature sprint</div>
              </div>
            </div>
            <a
              href={CAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="tcf-btn tcf-btn-gold"
              data-testid="founder-cta"
            >
              Book Direct With Me <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* Who this is for */}
      <section className="tcf-who" data-testid="tcf-who">
        <div className="tcf-who-inner">
          <span className="tcf-section-eyebrow">Who This Is For</span>
          <h2 className="tcf-section-title">
            Built for action-takers, not tire-kickers.
          </h2>
          <div className="tcf-who-grid">
            {WHO.map(({ icon: Icon, text }) => (
              <div className="tcf-who-item" key={text}>
                <span className="tcf-who-item-icon">
                  <Icon size={20} />
                </span>
                <span className="tcf-who-item-txt">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="tcf-testimonials" data-testid="tcf-testimonials">
        <div className="tcf-testimonials-head">
          <span className="tcf-section-eyebrow">Real Client Wins</span>
          <h2 className="tcf-section-title">
            Receipts. Not promises.
          </h2>
        </div>
        <div className="tcf-testimonials-grid">
          {TESTIMONIALS.map((t) => (
            <article
              key={t.name}
              className="tcf-testimonial"
              data-testid={`testimonial-${t.initials}`}
            >
              <div className="tcf-testimonial-quote-mark">“</div>
              <p className="tcf-testimonial-quote">{t.quote}</p>
              <span className="tcf-testimonial-result">{t.result}</span>
              <div className="tcf-testimonial-footer">
                <span
                  className="tcf-testimonial-avatar"
                  style={{ background: t.color }}
                >
                  {t.initials}
                </span>
                <div>
                  <div className="tcf-testimonial-name">{t.name}</div>
                  <div className="tcf-testimonial-loc">{t.location}</div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="tcf-final" data-testid="tcf-final">
        <div className="tcf-final-inner">
          <span className="tcf-section-eyebrow" style={{ color: 'var(--tca-gold)' }}>
            <Sparkles size={14} style={{ display: 'inline', marginRight: 6 }} />
            Ready to move
          </span>
          <h2 className="tcf-final-title">
            Stop guessing. <span className="gold">Start scaling.</span>
          </h2>
          <p className="tcf-final-sub">
            Book a private 30-minute strategy call. We audit your file, map the right stack, and
            hand you a step-by-step plan — no pressure, no upsell theatre.
          </p>
          <div className="tcf-final-ctas">
            <a
              href={CAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="tcf-btn tcf-btn-gold tcf-btn-lg"
              data-testid="final-cta-book"
            >
              <Clock size={18} /> Book My Strategy Call
            </a>
            <Link
              to="/services"
              className="tcf-btn tcf-btn-secondary tcf-btn-lg"
              style={{ borderColor: '#fff', color: '#fff' }}
              data-testid="final-cta-services"
            >
              Explore All Services
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="tcf-faq" data-testid="tcf-faq">
        <div className="tcf-faq-inner">
          <div className="tcf-faq-head">
            <span className="tcf-section-eyebrow">Frequently Asked</span>
            <h2 className="tcf-section-title">Questions we hear all the time.</h2>
          </div>
          {FAQS.map((f, i) => (
            <div
              key={f.q}
              className={`tcf-faq-item ${openFaq === i ? 'open' : ''}`}
              data-testid={`faq-${i}`}
            >
              <button
                className="tcf-faq-q"
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                data-testid={`faq-${i}-toggle`}
              >
                <span>{f.q}</span>
                <ChevronDown size={22} />
              </button>
              <div className="tcf-faq-a">{f.a}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Sticky mobile Book-a-Call CTA */}
      <a
        href={CAL_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={`tcf-sticky-cta ${stickyVisible ? 'visible' : ''}`}
        data-testid="sticky-book-call"
      >
        <Clock size={16} /> Book a Call
      </a>
    </div>
  );
};

export default HomePage;
